/**
 * Unit conversion + display helpers.
 *
 * The backend computes geo distances in kilometers (haversine in km, drive
 * speed in km/h). Groundwork Command operates in the United States, so the UI
 * always displays imperial units. Conversion happens at the rendering edge
 * — internal math stays metric to avoid round-trip drift.
 */

export const KM_TO_MILES = 0.621371;
export const MILES_TO_KM = 1 / KM_TO_MILES;
export const METERS_TO_FEET = 3.28084;

export function kmToMiles(km: number): number {
    return km * KM_TO_MILES;
}

export function milesToKm(mi: number): number {
    return mi * MILES_TO_KM;
}

export function metersToFeet(m: number): number {
    return m * METERS_TO_FEET;
}

/**
 * Format a distance value (input in km) as imperial:
 *   • < 0.1 mi  → feet, rounded to 10ft
 *   • < 10 mi   → 1 decimal
 *   • >= 10 mi  → integer
 */
export function formatMilesFromKm(km: number | null | undefined): string {
    if (km == null || !Number.isFinite(km)) return "—";
    const mi = kmToMiles(km);
    if (mi < 0.1) {
        const feet = Math.round((mi * 5280) / 10) * 10;
        return `${feet} ft`;
    }
    if (mi < 10) return `${mi.toFixed(1)} mi`;
    return `${Math.round(mi)} mi`;
}

/** Same as `formatMilesFromKm` but returns just the numeric portion. */
export function milesFromKm(km: number | null | undefined, digits = 1): number | null {
    if (km == null || !Number.isFinite(km)) return null;
    return Number((km * KM_TO_MILES).toFixed(digits));
}

/** Convert a km/h speed to mph for display. */
export function formatMphFromKmh(kmh: number | null | undefined): string {
    if (kmh == null || !Number.isFinite(kmh)) return "—";
    return `${Math.round(kmh * KM_TO_MILES)} mph`;
}
